import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  Globe,
  Users,
  ArrowLeft,
  Send,
  Loader2,
  Languages,
  MessageCircle,
  Sparkles,
  Lightbulb,
  Info,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { ScrollArea } from '../components/ui/scroll-area';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { toast } from 'sonner';

// Animated typing dots component
const TypingIndicator = ({ userName }) => (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -10 }}
    className="flex justify-start"
  >
    <div className="flex gap-3 max-w-[80%]">
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 bg-sand-beige text-ocean-blue">
        <span className="text-sm font-medium">{userName?.charAt(0).toUpperCase() || '?'}</span>
      </div>
      <div>
        <p className="text-xs mb-1 text-muted-foreground">{userName || 'Someone'}</p>
        <div className="p-4 rounded-2xl bg-card border border-border shadow-sm rounded-tl-sm">
          <div className="flex items-center gap-1" data-testid="typing-indicator">
            <motion.span
              className="w-2 h-2 bg-muted-foreground rounded-full"
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 0.6, repeat: Infinity, delay: 0 }}
            />
            <motion.span
              className="w-2 h-2 bg-muted-foreground rounded-full"
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 0.6, repeat: Infinity, delay: 0.2 }}
            />
            <motion.span
              className="w-2 h-2 bg-muted-foreground rounded-full"
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 0.6, repeat: Infinity, delay: 0.4 }}
            />
          </div>
        </div>
      </div>
    </div>
  </motion.div>
);

// Translation loading dots
const TranslatingDots = () => (
  <div className="flex items-center gap-1 text-xs text-muted-foreground mt-2">
    <Languages className="w-3 h-3" />
    <span>Translating</span>
    <motion.span
      animate={{ opacity: [0, 1, 0] }}
      transition={{ duration: 1.5, repeat: Infinity, delay: 0 }}
    >.</motion.span>
    <motion.span
      animate={{ opacity: [0, 1, 0] }}
      transition={{ duration: 1.5, repeat: Infinity, delay: 0.3 }}
    >.</motion.span>
    <motion.span
      animate={{ opacity: [0, 1, 0] }}
      transition={{ duration: 1.5, repeat: Infinity, delay: 0.6 }}
    >.</motion.span>
  </div>
);

const ChatRoom = () => {
  const { roomType } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  // Session-persisted auto-translate setting (defaults to user preference or true)
  const [autoTranslate, setAutoTranslate] = useState(() => {
    const saved = sessionStorage.getItem('chat_auto_translate');
    if (saved !== null) return saved === 'true';
    return user?.auto_translate !== undefined ? user.auto_translate : true;
  });
  const [showInsights, setShowInsights] = useState(true);
  const [typingUsers, setTypingUsers] = useState([]);
  const [translatingMessages, setTranslatingMessages] = useState(new Set());
  
  const scrollRef = useRef(null);
  const intervalRef = useRef(null);
  const typingIntervalRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingRef = useRef(null);

  const isGeneral = roomType === 'general';
  const roomId = isGeneral ? 'global' : user?.region || 'global';

  // Load messages without server-side translation (we'll do it client-side)
  const loadMessages = useCallback(async (silent = false) => {
    try {
      // Fetch messages WITHOUT auto-translate to get them fast
      const response = await api.getMessages(roomType, roomId, false);
      const newMessages = response.data.messages || [];
      
      setMessages(prevMessages => {
        // Check if there are actually new messages
        const prevIds = new Set(prevMessages.map(m => m.id));
        const hasNewMessages = newMessages.some(m => !prevIds.has(m.id));
        
        if (hasNewMessages || prevMessages.length !== newMessages.length) {
          // Preserve existing translations
          const translationCache = {};
          prevMessages.forEach(m => {
            if (m.translated_for_reader) {
              translationCache[m.id] = m.translated_for_reader;
            }
          });
          
          // Apply cached translations to new messages
          return newMessages.map(m => ({
            ...m,
            translated_for_reader: translationCache[m.id] || m.translated_for_reader || null
          }));
        }
        return prevMessages;
      });
    } catch (error) {
      if (!silent) {
        console.error('Error loading messages:', error);
      }
    }
  }, [roomType, roomId]);

  // Translate a single message on demand
  const translateMessage = useCallback(async (messageId, content, originalLanguage) => {
    if (!autoTranslate || !isGeneral) return;
    
    const userLang = user?.preferred_language || 'en';
    if (originalLanguage === userLang) return; // Already in user's language
    
    setTranslatingMessages(prev => new Set([...prev, messageId]));
    
    try {
      const response = await api.post('/chat/translate', {
        message_id: messageId,
        content,
        target_language: userLang
      });
      
      if (response.data.translation) {
        setMessages(prev => prev.map(m => 
          m.id === messageId 
            ? { ...m, translated_for_reader: response.data.translation }
            : m
        ));
      }
    } catch (error) {
      console.error('Translation error:', error);
    } finally {
      setTranslatingMessages(prev => {
        const next = new Set(prev);
        next.delete(messageId);
        return next;
      });
    }
  }, [autoTranslate, isGeneral, user?.preferred_language]);

  // Effect to translate new messages when they arrive
  useEffect(() => {
    if (!autoTranslate || !isGeneral) return;
    
    const userLang = user?.preferred_language || 'en';
    
    messages.forEach(msg => {
      // Skip own messages or already translated
      if (msg.user_id === user?.id) return;
      if (msg.translated_for_reader) return;
      if (msg.original_language === userLang) return;
      if (translatingMessages.has(msg.id)) return;
      
      // Translate this message
      translateMessage(msg.id, msg.content, msg.original_language);
    });
  }, [messages, autoTranslate, isGeneral, user?.id, user?.preferred_language, translateMessage, translatingMessages]);

  // Poll for messages
  useEffect(() => {
    loadMessages();
    intervalRef.current = setInterval(() => loadMessages(true), 2000);
    return () => clearInterval(intervalRef.current);
  }, [loadMessages]);

  // Poll for typing status
  useEffect(() => {
    const checkTyping = async () => {
      try {
        const response = await api.get(`/chat/${roomType}/${roomId}/typing`);
        const typingList = response.data.typing_users || [];
        // Filter out self
        setTypingUsers(typingList.filter(u => u.user_id !== user?.id));
      } catch (error) {
        // Silently fail
      }
    };
    
    checkTyping();
    typingIntervalRef.current = setInterval(checkTyping, 2000);
    return () => clearInterval(typingIntervalRef.current);
  }, [roomType, roomId, user?.id]);

  // Auto-scroll when new messages arrive
  useEffect(() => {
    if (scrollRef.current && messages.length > 0) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, typingUsers]);

  // Send typing indicator
  const sendTypingIndicator = useCallback(async () => {
    const now = Date.now();
    // Only send every 2 seconds to avoid spam
    if (lastTypingRef.current && now - lastTypingRef.current < 2000) return;
    lastTypingRef.current = now;
    
    try {
      await api.post(`/chat/${roomType}/${roomId}/typing`, {
        user_id: user?.id,
        user_name: user?.name
      });
    } catch (error) {
      // Silently fail
    }
  }, [roomType, roomId, user?.id, user?.name]);

  // Handle input change with typing indicator
  const handleInputChange = (e) => {
    setInput(e.target.value);
    
    if (e.target.value.trim()) {
      sendTypingIndicator();
      
      // Clear typing after 3 seconds of no input
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      typingTimeoutRef.current = setTimeout(() => {
        // Could send a "stopped typing" signal here if needed
      }, 3000);
    }
  };

  // Handle auto-translate toggle - persist to session
  const handleAutoTranslateToggle = (value) => {
    setAutoTranslate(value);
    sessionStorage.setItem('chat_auto_translate', value.toString());
    
    // If turning on, trigger translations for existing messages
    if (value) {
      setMessages(prev => prev.map(m => ({
        ...m,
        translated_for_reader: m.user_id === user?.id ? null : m.translated_for_reader
      })));
    }
  };

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const content = input.trim();
    setInput('');
    setLoading(true);

    try {
      await api.sendMessage(roomType, roomId, content, false);
      await loadMessages();
    } catch (error) {
      toast.error('Could not send message');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const insights = [
    {
      type: 'summary',
      title: 'Discussion Summary',
      content: 'Founders are discussing early traction strategies and customer discovery methods.'
    },
    {
      type: 'prompt',
      title: 'Suggested Question',
      content: 'What was the first thing you did to find your first 10 customers?'
    },
    {
      type: 'tip',
      title: 'Cultural Tip',
      content: 'In global discussions, be mindful of time zones when scheduling live sessions.'
    }
  ];

  const userLang = user?.preferred_language || 'en';

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className={`sticky top-0 z-50 ${isGeneral ? 'bg-sky-blue' : 'bg-coral'} text-white`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="text-white hover:bg-white/10"
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
                  {isGeneral ? <Globe className="w-6 h-6" /> : <Users className="w-6 h-6" />}
                </div>
                <div>
                  <h1 className="font-bold">{isGeneral ? t('nav.generalRoom') : t('nav.rooms')}</h1>
                  <p className="text-xs text-white/70">
                    {isGeneral ? t('chat.globalCommunity') : `${user?.region?.replace(/_/g, ' ')} ${t('chat.founders')}`}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Auto-Translate Toggle - More prominent */}
            {isGeneral && (
              <Button
                variant="ghost"
                onClick={() => handleAutoTranslateToggle(!autoTranslate)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full transition-all ${
                  autoTranslate 
                    ? 'bg-white/20 hover:bg-white/30' 
                    : 'bg-white/5 hover:bg-white/10'
                }`}
                data-testid="auto-translate-toggle"
              >
                <Languages className="w-4 h-4" />
                {autoTranslate ? (
                  <ToggleRight className="w-5 h-5 text-mint" />
                ) : (
                  <ToggleLeft className="w-5 h-5 opacity-60" />
                )}
                <span className="text-sm hidden sm:inline">
                  {autoTranslate ? 'Auto-translate ON' : 'Auto-translate OFF'}
                </span>
              </Button>
            )}
          </div>
        </div>
        <div className="wave-divider h-8" />
      </header>

      {/* Main Content */}
      <div className="flex-1 flex">
        {/* Chat Area */}
        <div className="flex-1 flex flex-col">
          <ScrollArea className="flex-1 p-4" ref={scrollRef}>
            <div className="max-w-3xl mx-auto space-y-4 pb-4">
              {messages.length === 0 ? (
                <div className="text-center py-12">
                  <MessageCircle className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="font-semibold text-foreground mb-2">{t('chat.noMessages')}</h3>
                  <p className="text-muted-foreground">{t('chat.beFirst')}</p>
                </div>
              ) : (
                <>
                  {messages.map((message, index) => {
                    const isOwn = message.user_id === user?.id;
                    const needsTranslation = !isOwn && 
                      autoTranslate && 
                      isGeneral && 
                      message.original_language !== userLang &&
                      !message.translated_for_reader;
                    const isTranslating = translatingMessages.has(message.id);
                    
                    return (
                      <motion.div
                        key={message.id || index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`flex gap-3 max-w-[80%] ${isOwn ? 'flex-row-reverse' : ''}`}>
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                            isOwn 
                              ? 'gradient-ocean text-white' 
                              : 'bg-sand-beige text-ocean-blue'
                          }`}>
                            <span className="text-sm font-medium">
                              {message.user_name?.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <p className={`text-xs mb-1 ${isOwn ? 'text-right' : ''} text-muted-foreground`}>
                              {isOwn ? 'You' : message.user_name}
                              {!isOwn && message.original_language && message.original_language !== userLang && (
                                <Badge variant="outline" className="ml-2 text-[10px] px-1 py-0">
                                  {message.original_language.toUpperCase()}
                                </Badge>
                              )}
                            </p>
                            <div className={`p-4 rounded-2xl ${
                              isOwn
                                ? 'gradient-ocean text-white rounded-tr-sm'
                                : 'bg-card border border-border shadow-sm rounded-tl-sm'
                            }`} data-testid={`chat-message-${index}`}>
                              {/* Show translated content if available and auto-translate is on */}
                              {!isOwn && autoTranslate && isGeneral && message.translated_for_reader ? (
                                <>
                                  <p className="whitespace-pre-wrap leading-relaxed">{message.translated_for_reader}</p>
                                  <div className="mt-3 pt-3 border-t border-border/30">
                                    <p className="text-xs opacity-60 mb-1 flex items-center gap-1">
                                      <Languages className="w-3 h-3" /> {t('chat.originalMessage')}
                                    </p>
                                    <p className="text-sm italic opacity-70">{message.content}</p>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <p className="whitespace-pre-wrap leading-relaxed">{message.content}</p>
                                  {/* Show translating indicator */}
                                  {isTranslating && <TranslatingDots />}
                                  {/* Show original translated_content if sender translated it */}
                                  {message.translated_content && isOwn && (
                                    <div className="mt-3 pt-3 border-t border-white/20">
                                      <p className="text-xs opacity-70 mb-1 flex items-center gap-1">
                                        <Languages className="w-3 h-3" /> {t('chat.translated')}
                                      </p>
                                      <p className="text-sm italic">{message.translated_content}</p>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                  
                  {/* Typing Indicators */}
                  <AnimatePresence>
                    {typingUsers.map((typingUser) => (
                      <TypingIndicator key={typingUser.user_id} userName={typingUser.user_name} />
                    ))}
                  </AnimatePresence>
                </>
              )}
            </div>
          </ScrollArea>

          {/* Input Area */}
          <div className="sticky bottom-0 bg-background border-t border-border p-4">
            <div className="max-w-3xl mx-auto">
              <div className="flex gap-3">
                <Input
                  value={input}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  placeholder={isGeneral ? t('chat.messageGlobal') : t('chat.messageRegional')}
                  className="flex-1 h-12 rounded-xl bg-card border-border"
                  disabled={loading}
                  data-testid="chat-input"
                />
                <Button
                  size="icon"
                  onClick={handleSend}
                  disabled={!input.trim() || loading}
                  className={`rounded-xl h-12 w-12 ${isGeneral ? 'bg-sky-blue hover:bg-sky-blue/90' : 'bg-coral hover:bg-coral/90'} text-white`}
                  data-testid="chat-send-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
              {isGeneral && (
                <p className="text-xs text-muted-foreground text-center mt-2">
                  {autoTranslate 
                    ? t('chat.autoTranslateEnabled')
                    : 'Auto-translate is off. Messages shown in original language.'
                  }
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Insights Panel (Desktop) */}
        {isGeneral && showInsights && (
          <aside className="hidden lg:block w-80 border-l border-border bg-card p-4 overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-foreground flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-ocean-blue" />
                Minnow Insights
              </h2>
              <Button variant="ghost" size="icon" onClick={() => setShowInsights(false)}>
                <Info className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="space-y-4">
              {insights.map((insight, index) => (
                <Card key={index} className="rounded-xl border-border">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      {insight.type === 'summary' && <MessageCircle className="w-4 h-4 text-ocean-blue" />}
                      {insight.type === 'prompt' && <Lightbulb className="w-4 h-4 text-coral" />}
                      {insight.type === 'tip' && <Globe className="w-4 h-4 text-mint" />}
                      {insight.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-muted-foreground">{insight.content}</p>
                    {insight.type === 'prompt' && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-3 w-full rounded-lg"
                        onClick={() => setInput(insight.content)}
                      >
                        Use this prompt
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </aside>
        )}
      </div>
    </div>
  );
};

export default ChatRoom;
